import { gql, NetworkStatus, useApolloClient, useQuery } from '@apollo/client'
import cx from 'classnames'
import { Form, Formik } from 'formik'
import { map } from 'lodash'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { FaSync } from 'react-icons/fa'
import Select, { MultiValue, OptionsOrGroups } from 'react-select'
import { StringParam, useQueryParam } from 'use-query-params'
import { BulmaMessage } from '../../Bulma'
import SelectField from '../../SelectField'
import TextInput from '../../TextInput'
import { recoveryCertificateStatuses } from './index'
import { friendlyConstant } from './RecoveryCertificate'
import { RecoveryCertificateLine } from './RecoveryCertificateLine'
import RecoveryCertificateLink from './RecoveryCertificateLink'
import useRecoveryCertificateEvent from './useRecoveryCertificateEvent'
import VerifyRecoveryCertificateModal from './VerifyRecoveryCertificateModal'
import {
  ClaimRecoveryCertificateMutation,
  ClaimRecoveryCertificateMutationVariables,
} from './__generated__/ClaimRecoveryCertificateMutation'
import {
  RecoveryCertificateQuery,
  RecoveryCertificateQueryVariables,
  RecoveryCertificateQuery_recoveryCertificates,
} from './__generated__/RecoveryCertificateQuery'

const recoveryCertificatesQuery = gql`
  query RecoveryCertificateListQuery(
    $orderId: ID
    $emailAddress: String
    $status: [RecoveryCertificateStatus]
    $unClaimed: Boolean
  ) {
    recoveryCertificates(orderId: $orderId, emailAddress: $emailAddress, status: $status, unClaimed: $unClaimed) {
      orderId
      UUID

      completingFor
      completingForFirstName
      completingForLastName
      completingForRelationshipToPatient
      firstName
      lastName
      dateOfBirth
      emailAddress
      telephoneNumber
      passportNumber

      travelToDestination
      travelToDestinationOtherDescription

      positiveCovidTestDate
      typeOfCovidTest
      covidSymptomsAroundTestDate
      lastTimeExperiencedSymptoms
      additionalInformation

      fileUrls

      notes
      status
      paymentStatus
      claimedBy
      claimedAt

      createdAt

      totalNumberOfResults
    }
  }
`

const claimRecoveryCertificateMutation = gql`
  mutation ClaimRecoveryCertificateMutation($input: ClaimRecoveryCertificateInput!) {
    claimRecoveryCertificate(input: $input) {
      orderId
      status
      claimedBy
      claimedAt
    }
  }
`

const toOptions = (statuses) =>
  statuses.map((currentStatus) => ({
    label: friendlyConstant(currentStatus),
    value: currentStatus,
  })) as OptionsOrGroups<RecoveryCertificateQuery_recoveryCertificates['status'], any>

const RecoveryCertificateViews = {
  review: [
    'NEW',
    'AWAITING_RESPONSE_FROM_CUSTOMER',
    'REVIEWING_DOCUMENTATION',
  ] as RecoveryCertificateQuery_recoveryCertificates['status'][],
  final_review: ['DOCUMENTATION_COMPLETE', 'FINAL_REVIEW'] as RecoveryCertificateQuery_recoveryCertificates['status'][],
}

type recoveryCertificates = RecoveryCertificateQuery_recoveryCertificates & {
  handled?: boolean
}

export const RecoveryCertificateList = ({
  orderId,
}: {
  orderId: RecoveryCertificateQuery_recoveryCertificates['orderId']
}) => {
  const client = useApolloClient()

  const [notificationMessage, setNotificationMessage] = useState<JSX.Element>()
  const [status, setStatus] = useState<recoveryCertificates['status'][]>(RecoveryCertificateViews['review'])
  const [selectedStatus, setSelectedStatus] = useState<MultiValue<recoveryCertificates['status']>>(
    toOptions(RecoveryCertificateViews['review'])
  )

  const [emailParam] = useQueryParam('emailAddress', StringParam)
  const [searchEmailAddress, setSearchEmailAddress] = useState<
    RecoveryCertificateQuery_recoveryCertificates['emailAddress']
  >(emailParam)

  const searchRecoveryCertificatesRef = useRef(null)
  const [searchOrderId, setSearchOrderId] = useState<recoveryCertificates['orderId']>(orderId)

  const { loading, data, refetch, networkStatus } = useQuery<
    RecoveryCertificateQuery,
    RecoveryCertificateQueryVariables
  >(recoveryCertificatesQuery, {
    variables: searchOrderId
      ? { orderId: searchOrderId }
      : searchEmailAddress
      ? { emailAddress: searchEmailAddress }
      : { status: status || undefined },
    notifyOnNetworkStatusChange: true,
  })

  const [recoveryCertificates, setRecoveryCertificates] = useState<recoveryCertificates[]>()

  useEffect(() => {
    refetch()
  }, [refetch, status])

  useEffect(() => {
    setRecoveryCertificates(data?.recoveryCertificates as recoveryCertificates[])
  }, [data])

  const { recoveryCertificateClaimed, recoveryCertificateHandled } = useRecoveryCertificateEvent()

  const updateRecoveryCertificates = ({ orderId, data }) => {
    if (
      recoveryCertificateClaimed?.orderId &&
      recoveryCertificateClaimed?.orderId !== verifyRecoveryCertificate &&
      recoveryCertificates
    ) {
      setRecoveryCertificates(
        recoveryCertificates.map((recoveryCertificate) => {
          if (recoveryCertificate.orderId === orderId) {
            recoveryCertificate = {
              ...recoveryCertificate,
              ...data,
            }
          }
          return recoveryCertificate
        })
      )
    }
  }

  // Pusher Event recoveryCertificateClaimed
  useEffect(() => {
    updateRecoveryCertificates({
      orderId: recoveryCertificateClaimed?.orderId,
      data: {
        claimedBy: recoveryCertificateClaimed?.claimedBy,
        claimedAt: recoveryCertificateClaimed?.claimedAt,
        status: recoveryCertificateClaimed?.status,
        paymentStatus: recoveryCertificateClaimed?.paymentStatus,
      },
    })
  }, [recoveryCertificateClaimed])

  // Pusher Event recoveryCertificateHandled
  useEffect(() => {
    updateRecoveryCertificates({
      orderId: recoveryCertificateClaimed?.orderId,
      data: {
        handled: true,
        claimedBy: recoveryCertificateClaimed?.claimedBy,
        claimedAt: recoveryCertificateClaimed?.claimedAt,
        status: recoveryCertificateClaimed?.status,
        paymentStatus: recoveryCertificateClaimed?.paymentStatus,
      },
    })
  }, [recoveryCertificateHandled])

  // Selected Recovery Certificate
  const [verifyRecoveryCertificate, setVerifyRecoveryCertificate2] = useState<recoveryCertificates['orderId']>()

  const setVerifyRecoveryCertificate = async (orderId) => {
    if (orderId) {
      client.mutate<ClaimRecoveryCertificateMutation, ClaimRecoveryCertificateMutationVariables>({
        mutation: claimRecoveryCertificateMutation,
        variables: { input: { orderId } },
      })
    }

    setVerifyRecoveryCertificate2(orderId)
  }

  const currentResults = recoveryCertificates?.length || 0
  const totalResults = (recoveryCertificates && recoveryCertificates[0]?.totalNumberOfResults) || 0

  const changeStatus = (selectedList) => {
    setSearchOrderId(undefined)
    setSearchEmailAddress(undefined)
    searchRecoveryCertificatesRef.current.setFieldValue('searchCriteria', '')

    setSelectedStatus(selectedList)
    setStatus(selectedList.map((item) => item.value))
  }

  const onUpdate = (status: recoveryCertificates['status'] = undefined) => {
    let message
    switch (status) {
      case 'NEW':
        message = 'Order has been unclaimed'
        break
      case 'AWAITING_RESPONSE_FROM_CUSTOMER':
        message = "Order has been marked as Awaiting Response from Customer, don't forget to contact the customer."
        break
      case 'DOCUMENTATION_COMPLETE':
        message = 'Order has been marked as APPROVED and is now awaiting FINAL REVIEW.'
        break
      case 'CANCELLED':
        message = (
          <>
            Order has been CANCELLED, don't forget to contact the customer and <b>ISSUE A REFUND IN STRIPE</b>.
          </>
        )
        break
      case 'APPROVED':
        message = 'Order has been APPROVED, recovery certificate has been emailed to the customer.'
        break
    }

    if (status) {
      setNotificationMessage(
        <>
          <p>
            <b>Changes Have Been Saved Successfully</b>
          </p>
          <br />
          <p>{message}</p>
          <br />
          <p>
            <RecoveryCertificateLink orderId={verifyRecoveryCertificate}>
              View Recovery Certificate
            </RecoveryCertificateLink>
          </p>
        </>
      )
    }

    setVerifyRecoveryCertificate(undefined)
    refetch()
  }

  const searchRecoveryCertificates = (data) => {
    switch (data.searchType) {
      case 'orderId':
        setSearchEmailAddress(undefined)
        setSearchOrderId(data.searchCriteria)
        break
      case 'emailAddress':
        setSearchOrderId(undefined)
        setSearchEmailAddress(data.searchCriteria)
        break
    }
  }

  return (
    <>
      <VerifyRecoveryCertificateModal recoveryCertificateId={verifyRecoveryCertificate} onClose={onUpdate} />

      <div className="title mb-3 has-text-centered">Recovery Certificates</div>

      {notificationMessage && <BulmaMessage color="info">{notificationMessage}</BulmaMessage>}

      <div className="navbar mb-2">
        <div className="navbar-brand">
          <div className="navbar-item">Show Results For:</div>
        </div>
        <div className="navbar-start"></div>
        <div className="navbar-menu">
          {map(RecoveryCertificateViews, (statuses, name) => (
            <div className="navbar-item" key={name}>
              <button
                className={`button ${
                  statuses.toString() === status.toString() && !searchOrderId
                    ? 'is-info  has-text-weight-bold'
                    : 'is-light'
                }`}
                style={statuses.toString() === status.toString() && !searchOrderId ? { cursor: 'default' } : undefined}
                onClick={() => changeStatus(toOptions(statuses))}
              >
                {friendlyConstant(name)}
              </button>
            </div>
          ))}
        </div>
        <div className="navbar-end">
          <div className="navbar-item">
            Showing {currentResults} of {totalResults} Results
          </div>
          <div className="navbar-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus === NetworkStatus.refetch,
              })}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </div>

      <table className="table is-bordered is-fullwidth is-striped is-size-6 has-valign-middle">
        <thead>
          <tr className="has-background-light">
            <th>Result Details</th>
            <th>Issue</th>
            <th>Status:</th>
            <th>Payment Status</th>
            <th>Actions</th>
          </tr>
          <tr className="has-background-light">
            <th style={{ maxWidth: '180px' }}>
              <Formik
                onSubmit={searchRecoveryCertificates}
                initialValues={{
                  searchType: searchEmailAddress ? 'emailAddress' : 'orderId',
                  searchCriteria: searchOrderId || searchEmailAddress,
                }}
                innerRef={searchRecoveryCertificatesRef}
              >
                <Form style={{ overflow: 'hidden' }}>
                  <div className="is-flex is-align-items-center" style={{ minWidth: '120px' }}>
                    Search
                    <SelectField name="searchType" className="is-size-6 ml-3 is-fullwidth">
                      <option value="orderId">Order Id</option>
                      <option value="emailAddress">Email Address</option>
                    </SelectField>
                  </div>
                  <TextInput name="searchCriteria" placeholder="Search Criteria" />
                </Form>
              </Formik>
            </th>
            <th></th>
            <th style={{ maxWidth: '300px' }}>
              <Select
                isMulti
                options={toOptions(recoveryCertificateStatuses.filter((value) => value !== 'APPROVED'))}
                onChange={changeStatus}
                value={selectedStatus}
              />
            </th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td>loading...</td>
            </tr>
          ) : recoveryCertificates?.length === 0 ? (
            <tr>
              <td>No Results</td>
            </tr>
          ) : (
            recoveryCertificates?.map((recoveryCertificate, key) => (
              <RecoveryCertificateLine
                key={key}
                recoveryCertificate={recoveryCertificate}
                onReviewDocumentation={() => setVerifyRecoveryCertificate(recoveryCertificate.orderId)}
              />
            ))
          )}
        </tbody>
      </table>
    </>
  )
}

export default RecoveryCertificateList
